/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect } from 'react';

import config from 'js/app/config';
import useRouter from 'js/lib/useRouter';

import { breakpoints } from '@coursera/cds-core';
import { CardV2 } from '@coursera/coursera-ui';

import Header from 'bundles/authentication-common/components/Header';
import AuthenticationContent from 'bundles/authentication-common/components/authenticationModal/AuthenticationContent';
import { AUTHENTICATION_MODAL_MODES, HEADER_HEIGHT_PX } from 'bundles/authentication-common/constants';
import {
  impressIntoProgressiveAuthExperiment,
  shouldShowProgressiveAuth,
} from 'bundles/authentication-common/utils/experimentUtils';
import { getAbsoluteRedirectToURL, toValidRedirect } from 'bundles/authentication-common/utils/navigationUtils';
import ConsumerLoggedOutHomePageHeader from 'bundles/composable-page-header-consumer/header-renderers/ConsumerLoggedOutHomePageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import GlobalFooter from 'bundles/page-footer/components/global-footer/GlobalFooter';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import _t from 'i18n!nls/authentication';

const getStyles = (isProgressiveAuthEnabled: boolean) => ({
  cardContainer: css`
    background-color: ${isProgressiveAuthEnabled ? 'var(--cds-color-white-0)' : 'var(--cds-color-blue-50)'};
    margin: auto;
    width: 100%;
    padding: var(--cds-spacing-400);
    ${breakpoints.down('xs')} {
      padding: 0;
      overflow-y: hidden;
    }

    .standalone-auth-card {
      max-width: 518px;
      background-color: var(--cds-color-neutral-background-primary);
      margin: auto;

      ${breakpoints.up('sm')} {
        padding: var(--cds-spacing-300) 0;
      }

      ${breakpoints.down('sm')} {
        width: 80%;
      }

      ${breakpoints.down('xs')} {
        width: 100%;
        max-width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        padding-top: ${HEADER_HEIGHT_PX}px;
      }
    }
  `,
  hideOnMobile: css`
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
});

function StandaloneAuthPage() {
  const router = useRouter();
  const { pathname } = router.location;
  const isLogin = pathname.includes('/login');

  const redirectTo = toValidRedirect(router.location.query.redirectTo || router.location.query.r);

  const isEnterpriseLogin =
    !!router.location.query.authProvider || (!!redirectTo && redirectTo.startsWith('/programs'));

  const getIsInternalToolAuthUsage = () => {
    const internalToolPatterns = ['tools.coursera.org', 'cds-storybook'];

    if (!redirectTo) {
      return false;
    }

    try {
      const url = new URL(redirectTo, config.url.base);
      const fullUrl = `${url.hostname}${url.pathname}`;
      return internalToolPatterns.some((pattern) => fullUrl.includes(pattern) || redirectTo.includes(pattern));
    } catch {
      // Fallback for invalid URLs - check against redirect string directly
      return internalToolPatterns.some((pattern) => redirectTo.includes(pattern));
    }
  };

  const shouldDisableProgressiveAuth = isEnterpriseLogin || getIsInternalToolAuthUsage();
  const showProgressiveAuth = shouldShowProgressiveAuth({
    shouldDisableProgressiveAuth,
  });

  const context = {
    action: router.location.query.action,
    accountType: router.location.query.serviceCode,
    mode: router.location.query.completeMode,
    service: router.location.query.authType,
    showProgressiveAuth,
  };

  const metaTitle = isLogin ? _t('Coursera Login - Continue Learning') : _t('Coursera Signup - Start Learning');
  const metaDescription = isLogin
    ? _t(
        'Log into your Coursera account with your email address, Google, Facebook, or Apple credential. Learn online and earn valuable credentials from top universities like Yale, Michigan, Stanford, and leading companies like Google and IBM.'
      )
    : _t(
        'Join Coursera for free. Create a profile to get a personalized learning experience with course recommendations.'
      );

  const seoMetadata = {
    title: metaTitle,
    ogAndTwitterTitle: metaTitle,
    description: metaDescription,
    disableCourseraDescriptionSuffix: true,
  };

  useEffect(() => {
    if (!shouldDisableProgressiveAuth) {
      impressIntoProgressiveAuthExperiment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const styles = getStyles(showProgressiveAuth);

  return (
    <div className={isLogin ? 'rc-LoginPage' : 'rc-SignupPage'} key={isLogin ? 'login-page' : 'signup-page'}>
      <section css={styles.hideOnMobile}>
        <ConsumerLoggedOutHomePageHeader hideAuthButtons />
        <UniversalPageFeatures authModalOptions={{ disableAuth: true }} />
      </section>
      <div css={styles.cardContainer} data-e2e="auth-modal">
        <CardV2 rootClassName="standalone-auth-card" isInteractive={false}>
          <Header logoAsLink linkTrackingName="auth_header_logo" />
          <AuthenticationContent
            isStandaloneAuth
            context={context}
            defaultMode={isLogin ? AUTHENTICATION_MODAL_MODES.LOGIN : AUTHENTICATION_MODAL_MODES.SIGNUP}
            redirectTo={redirectTo}
            magicLinkRedirect={getAbsoluteRedirectToURL(redirectTo)}
          />
        </CardV2>
      </div>
      <section css={styles.hideOnMobile}>
        <GlobalFooter />
      </section>
      <CourseraMetatags metaNameAndProperties={seoMetadata} />
    </div>
  );
}

export default StandaloneAuthPage;
